/* @flow */

import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import NumberFormat from 'react-number-format';
import type { Fakt } from './types';
import { ZNCODE } from './types';
import { useMediaQuery } from '../../../../javascripts/hooks/useMediaquery';
import { MEDIA_QUERIES } from '../../../../javascripts/constants';

export type Props = {
  data?: Fakt[],
  activeState?: Fakt,
  onStateChange: (state?: Fakt, stayFocussed?: boolean) => void,
  numberProps: {
    thousandSeparator?: string,
    decimalSeparator: string,
    decimalScale: number,
    suffix: string,
    displayType: string,
  },
  average?: number,
  orderascending?: boolean,
}

const Diagram = ({
  data, activeState, numberProps, onStateChange, average, orderascending,
}: Props): React$Element<"div"> => {
  const isDesktop = useMediaQuery(MEDIA_QUERIES.headernav);
  const [sortedData, setSortedData] = useState(Object.keys(ZNCODE)
    .map(i => ({ amount: null, faktregion: { zncode: i, title: ZNCODE[i] } })));
  const maxBarHeight = 150;

  useEffect(() => {
    if (data) {
      const sorted = data.sort((a, b) => {
        if (a.amount === null) {
          return 1;
        }
        if (b.amount === null) {
          return -1;
        }

        return (b.amount - a.amount);
      });

      if (orderascending) {
        sorted.reverse();
      }

      Object.keys(ZNCODE).forEach((code) => {
        const hasAmount = sorted.find(i => i.faktregion.zncode === code);

        if (!hasAmount) {
          const emptyAmount = { amount: null, faktregion: { zncode: code, title: ZNCODE[code] } };
          if (orderascending) {
            sorted.unshift(emptyAmount);
          } else {
            sorted.push(emptyAmount);
          }
        }
      });

      setSortedData(sorted);
    }
  }, [data]);

  const getHeight = (amountOf: number | null) => {
    const amount = amountOf || 0;
    const highestValue = orderascending ?
      (sortedData[sortedData.length - 1].amount || 0) : (sortedData[0].amount || 0);

    if (amount <= 0) {
      return 0.05 * maxBarHeight;
    }
    return (amount / highestValue) * maxBarHeight;
  };

  const handleHover = (i?: number) => {
    if (!isDesktop) {
      return;
    }
    const stateData = i !== undefined && sortedData[i];

    if (stateData && stateData.faktregion.zncode !== 'BW') {
      onStateChange(stateData);
    } else {
      onStateChange(undefined);
    }
  };

  return (
    <div className='map-is__diagram'>
      {average !== undefined && <div className='map-is__average' style={{ height: `${getHeight(average)}px` }}>
        <span className='map-is__average-text'>
          <NumberFormat
            {...numberProps}
            value={average}
            prefix='Ø '
            />
        </span>
      </div>}
      <div className='map-is__diagram-inner'>
        <dl className='map-is__chart' style={{ height: `${maxBarHeight}px` }}>
          {Object.keys(ZNCODE).map((code, i) => (
            <button
              key={i}
              className={classNames(
                'map-is__bar-container',
                sortedData[i].faktregion.zncode === 'BW' && 'map-is__bar-container--bw',
                activeState && (sortedData[i].faktregion.zncode === activeState.faktregion.zncode) && 'map-is__bar-container--active',
              )}
              type='button'
              onMouseEnter={() => handleHover(i)}
              onMouseLeave={() => handleHover(undefined)}
              onClick={sortedData[i].faktregion.zncode !== 'BW' ? () => onStateChange(sortedData[i], true) : undefined}
            >
              <dd
                className={classNames('map-is__bar', sortedData[i].amount === null && 'map-is__bar--disabled')}
                style={{ height: `${getHeight(sortedData[i].amount)}px` }}>
                  <span className='u-hidden-visually'>{sortedData[i].amount}</span>
              </dd>
              <div className='map-is__bar-label'>
                <dt className='map-is__bar-label-text'>{ZNCODE[sortedData[i].faktregion.zncode]}</dt>
              </div>
            </button>
          ))}
        </dl>
      </div>

    </div>
  );
};

export default Diagram;
